import React, { useState } from "react";
import { UserCircle } from "lucide-react";
import { useAtom } from "jotai";
import { appConstants } from "../../atomstore/store";
import SpinnerLoading from "../common/SpinnerLoading";

const ParentSelectModal = ({
    motherName,
    fatherName,
    motherPhoto,
    fatherPhoto,
    onSelect,
    isLoading,
    error,
}) => {
    const [constants] = useAtom(appConstants);
    const [selectedParent, setSelectedParent] = useState(null);
    const [imageErrors, setImageErrors] = useState({ mother: false, father: false });

    const getFullPath = (path) => {
        if (typeof path === "string" && path.startsWith("https")) return path;
        if (typeof path === "string" && path.length === 0) return path;
        return constants?.ImagePath + path;
    };

    const handleSelect = (parentType) => {
        if (isLoading) return;
        setSelectedParent(parentType);
        onSelect(parentType);
    };

    const parents = [
        {
            type: "Mother",
            name: motherName || "Mother",
            photo: motherPhoto,
            imageError: imageErrors.mother,
            onImageError: () => setImageErrors((prev) => ({ ...prev, mother: true })),
        },
        {
            type: "Father",
            name: fatherName || "Father",
            photo: fatherPhoto,
            imageError: imageErrors.father,
            onImageError: () => setImageErrors((prev) => ({ ...prev, father: true })),
        },
    ];

    return (
        <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="parent-select-title"
        >
            {isLoading && <SpinnerLoading />}
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative">
                <h2
                    id="parent-select-title"
                    className="text-xl font-semibold text-gray-800 mb-2 text-center"
                >
                    Who is logging in?
                </h2>
                <p className="text-sm text-gray-500 text-center mb-6">
                    Both parents share the same login credentials.
                    Please select who you are.
                </p>

                <div className="grid grid-cols-2 gap-4">
                    {parents.map(({ type, name, photo, imageError, onImageError }) => {
                        const isSelected = selectedParent === type && isLoading;
                        const isDisabled = isLoading && selectedParent !== type;

                        return (
                            <button
                                key={type}
                                onClick={() => handleSelect(type)}
                                disabled={isLoading}
                                aria-label={`Log in as ${type} - ${name}`}
                                className={`cursor-pointer bg-white rounded-2xl p-4 shadow-sm border transition
                                    ${isSelected ? "border-cyan-500 shadow-md" : "border-gray-100"}
                                    ${isDisabled ? "opacity-50 pointer-events-none" : ""}
                                    ${!isLoading ? "hover:shadow-md hover:border-gray-200 active:scale-[0.98]" : ""}
                                `}
                            >
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`w-20 h-20 rounded-xl bg-gradient-to-br from-cyan-100 to-blue-100 flex items-center justify-center overflow-hidden border-[4px]
                                            ${isSelected ? "border-cyan-500" : "border-transparent"}
                                        `}
                                    >
                                        {photo && !imageError ? (
                                            <img
                                                className="w-full h-full object-cover"
                                                src={getFullPath(photo)}
                                                alt={name}
                                                onError={onImageError}
                                            />
                                        ) : (
                                            <div className="w-full h-full rounded-full flex items-center justify-center text-gray-600 font-bold text-lg">
                                                <UserCircle size={50} />
                                            </div>
                                        )}
                                    </div>
                                    <p className="mt-3 font-semibold text-gray-800 text-center">
                                        {name}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">{type}</p>
                                    {isSelected && (
                                        <p className="text-xs text-cyan-700 mt-1 font-medium">
                                            Logging in...
                                        </p>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {error && (
                    <div
                        className="mt-4 bg-red-50 rounded-lg p-3 text-center"
                        role="alert"
                        aria-live="assertive"
                    >
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ParentSelectModal;
